import { useCallback, useEffect, useMemo, useState } from "react"
import { MessageSquare } from "lucide-react"
import { PaginationState } from "@tanstack/react-table"
import { useGetWebsiteChatSessionsQuery } from "../../../../redux/queries"
import { DateRangePreset } from "../../activity/date-filter"
import { dateRangeToTimestamps } from "../../../utils/datetime"
import { WebsiteChatSession } from "../../../../types"
import { useUserRoles, useBrandMap } from "../../../hooks"
import { withMessageThreads } from "../../../hooks/use-website-chat-conversations"
import WebsiteChatsTable from "./website-chats-table"

const PAGE_SIZE = 25

const WebsiteChatsTab = () => {
  const { isSuperAdmin } = useUserRoles()

  const { brandMap, isBrandsLoading } = useBrandMap()

  const { data: sessionsPage, isLoading } = useGetWebsiteChatSessionsQuery({
    page: 1,
    limit: 1000
  })

  const allSessions = useMemo(
    () =>
      withMessageThreads(
        (sessionsPage?.conversations ?? []) as WebsiteChatSession[]
      ),
    [sessionsPage?.conversations]
  )

  const [searchQuery, setSearchQuery] = useState("")
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchQuery, datePreset, customDateStart, customDateEnd])

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  const filteredSessions = useMemo<WebsiteChatSession[]>(() => {
    const q = searchQuery.trim().toLowerCase()
    const { min, max } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )

    return allSessions.filter(s => {
      if (q) {
        const brandName = isSuperAdmin
          ? (brandMap[s.brandId ?? ""]?.name ?? "").toLowerCase()
          : ""
        if (
          !s.subjectId.toLowerCase().includes(q) &&
          !(isSuperAdmin && brandName.includes(q))
        )
          return false
      }

      if (min || max) {
        const t = new Date(s.lastActivityAt).getTime()
        if (min && t < min) return false
        if (max && t > max) return false
      }

      return true
    })
  }, [
    allSessions,
    searchQuery,
    brandMap,
    datePreset,
    customDateStart,
    customDateEnd
  ])

  const paginatedSessions = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return filteredSessions.slice(start, start + pagination.pageSize)
  }, [filteredSessions, pagination.pageIndex, pagination.pageSize])

  const pageCount = Math.ceil(filteredSessions.length / PAGE_SIZE)

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-10 rounded-lg bg-neutral-100 w-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-14 rounded-lg bg-neutral-50 border border-neutral-100"
          />
        ))}
      </div>
    )
  }

  if (!allSessions.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-purple-50">
          <MessageSquare className="w-7 h-7 text-purple-300" />
        </div>
        <p className="text-sm font-semibold text-neutral-600">
          No website chat sessions yet
        </p>
        <p className="text-xs text-neutral-400 max-w-xs">
          When visitors start chats on your website, their sessions will appear
          here.
        </p>
      </div>
    )
  }

  return (
    <WebsiteChatsTable
      sessions={paginatedSessions}
      brandMap={isSuperAdmin ? brandMap : {}}
      isBrandsLoading={isSuperAdmin && isBrandsLoading}
      isLoading={isLoading}
      totalRows={filteredSessions.length}
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      datePreset={datePreset}
      customDateStart={customDateStart}
      customDateEnd={customDateEnd}
      onDateChange={handleDateChange}
      onClearDate={handleClearDate}
    />
  )
}

export default WebsiteChatsTab
